import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { rfidService, usersService, getErrorMessage } from '../services/api';
import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardContent,
  NativeInput,
  NativeSelect,
  NativeBadge,
  NativeAlert,
  NativeModal,
  NativeTable,
  NativeTableHeader,
  NativeTableHead,
  NativeTableBody,
  NativeTableRow,
  NativeTableCell,
  EmptyState
} from '../components/NativeComponents';
import { 
  Loader2, 
  Plus, 
  Pencil, 
  Trash2,
  Search,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  CreditCard,
  PlusCircle,
  Radio,
  Clock,
  Check,
  X,
  FileText,
  User,
  Home,
  TrendingUp,
  TrendingDown,
  Zap,
  ArrowLeft,
  Banknote,
  Building,
  CreditCard as CreditCardIcon,
  Ban,
  CheckCircle,
  Download,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

export default function RFIDPage() {
  const navigate = useNavigate();
  const { user, isAdmin, isSiteSakini, isSimulationActive, getEffectiveRole, getEffectiveSiteId, isHesapYoneticisi } = useAuth();
  
  // Simülasyon desteği
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Simülasyon için ek rol kontrolleri
  const isEffectiveSiteSakini = effectiveRole === ROLES.SITE_SAKINI || effectiveRole === ROLES.USER || effectiveRole === 'site_sakini';
  const isEffectiveCalisan = effectiveRole === ROLES.CALISAN || effectiveRole === 'calisan';
  
  // Site Sakini /rfid sayfasına erişemez - resident dashboard'a yönlendir
  useEffect(() => {
    if (isEffectiveSiteSakini || (isSiteSakini && isSiteSakini())) {
      navigate('/resident/dashboard', { replace: true });
    }
  }, [isEffectiveSiteSakini, isSiteSakini, navigate]);
  
  // Site sakini veya çalışan modunda butonları gizle
  const hideAdminButtons = isEffectiveSiteSakini || isEffectiveCalisan || (isSiteSakini && isSiteSakini());
  
  // Son okutulan kartlar
  const [recentScans, setRecentScans] = useState({ scans: [], total: 0, registered_count: 0, unregistered_count: 0 });
  const [recentScansLoading, setRecentScansLoading] = useState(false);
  
  // Son okutulanlardan seç modal
  const [selectFromScansModal, setSelectFromScansModal] = useState({ isOpen: false, unregisteredCards: [] });
  
  const [modal, setModal] = useState({ isOpen: false, mode: 'create', cardId: null, saving: false });
  const [creditModal, setCreditModal] = useState({ 
    isOpen: false, 
    card: null, 
    amount: '', 
    paymentMethod: 'cash',
    note: '',
    ownerInfo: null,
    saving: false 
  });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, card: null, deleting: false });
  
  // Ekstre modal
  const [statementModal, setStatementModal] = useState({ 
    isOpen: false, 
    cardId: null, 
    data: null, 
    loading: false 
  });
  
  const [form, setForm] = useState({ 
    card_id: '', 
    user_id: '', 
    status: 'active', 
    balance: 0,
    card_type: 'prepaid',
    monthly_limit: 0
  });

  // Yeni sakin + kart modal
  const [newResidentModal, setNewResidentModal] = useState({
    isOpen: false,
    saving: false,
    form: {
      full_name: '',
      email: '',
      apartment_number: '',
      password: '',
      card_id: '',
      card_type: 'prepaid',
      balance: 0
    }
  });

  // Ay sonu raporu modal
  const [monthlyReportModal, setMonthlyReportModal] = useState({
    isOpen: false,
    loading: false,
    data: null
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      let [cardsData, usersData] = await Promise.all([
        rfidService.list(),
        isAdmin() ? usersService.list() : Promise.resolve([])
      ]);
      
      // API response kontrolü - array veya obje olabilir
      let cardsArray = Array.isArray(cardsData) ? cardsData : (cardsData?.cards || cardsData?.items || cardsData?.data || []);
      let usersArray = Array.isArray(usersData) ? usersData : (usersData?.users || usersData?.items || usersData?.data || []);
      
      // Çalışan/Şoför için sadece kendisine atanmış kartları göster
      if (isEffectiveCalisan && user?.id) {
        cardsArray = cardsArray.filter(card => card.user_id === user.id);
      }
      
      // Hesap Yöneticisi için sadece kendi sitesinin kartlarını göster
      if (isEffectiveHesapYoneticisi && effectiveSiteId) {
        cardsArray = cardsArray.filter(card => card.site_id === effectiveSiteId);
        usersArray = usersArray.filter(u => u.site_id === effectiveSiteId);
      }
      
      setCards(cardsArray);
      setUsers(usersArray);
      setError(null);
    } catch (err) {
      console.error('RFID verileri yüklenemedi:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isEffectiveCalisan, user?.id, isEffectiveHesapYoneticisi, effectiveSiteId]);

  // Son okutulan kartları çek
  const fetchRecentScans = useCallback(async () => {
    try {
      setRecentScansLoading(true);
      const data = await rfidService.recentScans(20);
      setRecentScans(data || { scans: [], total: 0, registered_count: 0, unregistered_count: 0 });
    } catch (err) {
      console.error('Recent scans error:', err);
    } finally {
      setRecentScansLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchRecentScans();
    
    // Her 30 saniyede güncelle
    const interval = setInterval(fetchRecentScans, 30000);
    return () => clearInterval(interval);
  }, [fetchData, fetchRecentScans]);

  const openCreateModal = () => {
    setForm({ card_id: '', user_id: '', status: 'active', balance: 0, card_type: 'prepaid', monthly_limit: 0 });
    setModal({ isOpen: true, mode: 'create', cardId: null, saving: false });
  };

  // Kayıtsız karttan direkt modal aç
  const openCreateFromScan = (scan) => {
    setForm({ card_id: scan.id_tag, user_id: '', status: 'active', balance: 0, card_type: 'prepaid', monthly_limit: 0 });
    setModal({ isOpen: true, mode: 'create', cardId: null, saving: false });
  };

  const openEditModal = (card) => {
    setForm({ 
      card_id: card.card_id || '', 
      user_id: card.user_id || '', 
      status: card.status || 'active', 
      balance: card.balance || 0,
      card_type: card.card_type || 'prepaid',
      monthly_limit: card.monthly_limit || 0
    });
    setModal({ isOpen: true, mode: 'edit', cardId: card.card_id, saving: false });
  };

  const closeModal = () => {
    if (modal.saving) return;
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  // Son okutulanlardan seç
  const openSelectFromScansModal = async () => {
    try {
      const data = await rfidService.unregisteredScans(10);
      setSelectFromScansModal({ isOpen: true, unregisteredCards: data?.cards || [] });
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const selectCardFromScans = (card) => {
    setForm(prev => ({ ...prev, card_id: card.id_tag }));
    setSelectFromScansModal({ isOpen: false, unregisteredCards: [] });
    toast.success('Kart numarası forma eklendi');
  };

  // Bakiye yükleme modalını aç - kart sahibi bilgilerini al
  const openCreditModal = async (card) => {
    setCreditModal({ 
      isOpen: true, 
      card: card, 
      amount: '', 
      paymentMethod: 'cash',
      note: '',
      ownerInfo: null,
      saving: false 
    });
    
    // Kart sahibi bilgilerini arka planda al
    try {
      const ownerInfo = await rfidService.getOwnerInfo(card.card_id);
      setCreditModal(prev => ({ ...prev, ownerInfo }));
    } catch (err) {
      console.error('Owner info error:', err);
    }
  };

  const closeCreditModal = () => {
    if (creditModal.saving) return;
    setCreditModal({ isOpen: false, card: null, amount: '', paymentMethod: 'cash', note: '', ownerInfo: null, saving: false });
  };

  // Ekstre modalını aç
  const openStatementModal = async (cardId) => {
    setStatementModal({ isOpen: true, cardId, data: null, loading: true });
    
    try {
      const data = await rfidService.getStatement(cardId);
      setStatementModal(prev => ({ ...prev, data, loading: false }));
    } catch (err) {
      toast.error(getErrorMessage(err));
      setStatementModal(prev => ({ ...prev, loading: false }));
    }
  };

  const closeStatementModal = () => {
    setStatementModal({ isOpen: false, cardId: null, data: null, loading: false });
  };

  const openDeleteModal = (card) => {
    setDeleteModal({ isOpen: true, card: card, deleting: false });
  };

  const closeDeleteModal = () => {
    if (deleteModal.deleting) return;
    setDeleteModal({ isOpen: false, card: null, deleting: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (modal.saving) return;
    setModal(prev => ({ ...prev, saving: true }));
    
    try {
      const payload = {
        card_id: form.card_id,
        user_id: isAdmin() ? form.user_id || null : user?.id,
        status: form.status,
        balance: parseFloat(form.balance) || 0,
        card_type: form.card_type || 'prepaid',
        monthly_limit: form.card_type === 'postpaid' ? parseFloat(form.monthly_limit) || 0 : 0
      };

      if (modal.mode === 'create') {
        await rfidService.create(payload);
        toast.success('RFID kart başarıyla oluşturuldu');
      } else {
        await rfidService.update(modal.cardId, payload);
        toast.success('RFID kart basariyla guncellendi');
      }
      
      setModal({ isOpen: false, mode: 'create', cardId: null, saving: false });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setModal(prev => ({ ...prev, saving: false }));
    }
  };

  const handleCreditSubmit = async (e) => {
    e.preventDefault();
    if (creditModal.saving || !creditModal.card) return;
    setCreditModal(prev => ({ ...prev, saving: true }));
    
    try {
      const amount = parseFloat(creditModal.amount);
      if (isNaN(amount) || amount <= 0) {
        toast.error('Gecerli bir tutar girin');
        setCreditModal(prev => ({ ...prev, saving: false }));
        return;
      }
      
      await rfidService.credit(creditModal.card.card_id, { 
        amount,
        payment_method: creditModal.paymentMethod,
        note: creditModal.note || null
      });
      toast.success(`${amount.toFixed(2)} TL bakiye yüklendi`);
      setCreditModal({ isOpen: false, card: null, amount: '', paymentMethod: 'cash', note: '', ownerInfo: null, saving: false });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setCreditModal(prev => ({ ...prev, saving: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.card || deleteModal.deleting) return;
    setDeleteModal(prev => ({ ...prev, deleting: true }));
    
    try {
      await rfidService.delete(deleteModal.card.card_id);
      toast.success('RFID kart basariyla silindi');
      setDeleteModal({ isOpen: false, card: null, deleting: false });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setDeleteModal(prev => ({ ...prev, deleting: false }));
    }
  };

  // Kart bloke/aktif etme
  const toggleCardStatus = async (card) => {
    try {
      // Mevcut duruma göre yeni durumu belirle - hem status hem is_active kontrol et
      const isCurrentlyBlocked = card.status === 'blocked' || card.is_active === false;
      const newStatus = isCurrentlyBlocked ? 'active' : 'blocked';
      await rfidService.update(card.card_id, { status: newStatus });
      toast.success(isCurrentlyBlocked ? 'Kart aktif edildi' : 'Kart bloke edildi');
      // Liste verilerini yeniden yükle
      await fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Kartın bloke olup olmadığını kontrol et - hem status hem is_active kontrol et
  const isCardBlocked = (card) => {
    return card.status === 'blocked' || (card.status !== 'active' && card.is_active === false);
  };

  // Kartın aktif olup olmadığını kontrol et (active veya is_active)
  const isCardActive = (card) => {
    if (card.status === 'blocked') return false;
    if (card.status === 'active') return true;
    return card.is_active === true;
  };

  // Yeni sakin + kart oluştur
  const handleCreateResident = async (e) => {
    e.preventDefault();
    if (newResidentModal.saving) return;
    setNewResidentModal(prev => ({ ...prev, saving: true }));

    try {
      const { form: f } = newResidentModal;
      
      // Önce kullanıcı oluştur
      const userData = {
        email: f.email,
        full_name: f.full_name,
        password: f.password || 'Hims2024!',
        role: 'user',
        apartment_number: f.apartment_number
      };
      
      const newUser = await usersService.create(userData);
      
      // Sonra kart oluştur (eğer kart ID girilmişse)
      if (f.card_id) {
        const cardData = {
          card_id: f.card_id,
          user_id: newUser.id,
          status: 'active',
          card_type: f.card_type,
          balance: f.card_type === 'prepaid' ? parseFloat(f.balance) || 0 : 0
        };
        await rfidService.create(cardData);
      }
      
      toast.success('Site Sakini ve kart başarıyla oluşturuldu');
      setNewResidentModal({
        isOpen: false,
        saving: false,
        form: { full_name: '', email: '', apartment_number: '', password: '', card_id: '', card_type: 'prepaid', balance: 0 }
      });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setNewResidentModal(prev => ({ ...prev, saving: false }));
    }
  };

  // Ay sonu raporu oluştur
  const generateMonthlyReport = () => {
    const postpaidCards = cards.filter(c => c.card_type === 'postpaid');
    const reportData = postpaidCards.map(card => ({
      card_id: card.card_id,
      user_name: getUserName(card.user_id),
      apartment: getUserApartment(card.user_id) || '-',
      monthly_usage: card.monthly_usage || 0,
      monthly_limit: card.monthly_limit || 0,
      is_paid: false
    }));
    
    setMonthlyReportModal({
      isOpen: true,
      loading: false,
      data: reportData
    });
  };

  // PDF olarak indir
  const downloadReportPDF = () => {
    const { data } = monthlyReportModal;
    if (!data) return;

    const now = new Date();
    const monthNames = ['Ocak', 'Subat', 'Mart', 'Nisan', 'Mayis', 'Haziran', 'Temmuz', 'Agustos', 'Eylul', 'Ekim', 'Kasim', 'Aralik'];
    const monthName = monthNames[now.getMonth()];
    const year = now.getFullYear();

    // HTML tabanlı basit rapor oluştur
    let html = `
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Faturali Kartlar Raporu - ${monthName} ${year}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1e3a5f; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background-color: #1e3a5f; color: white; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .total { font-weight: bold; background-color: #e0f2fe; }
        </style>
      </head>
      <body>
        <h1>Faturali Kartlar Ay Sonu Raporu</h1>
        <p><strong>Donem:</strong> ${monthName} ${year}</p>
        <p><strong>Oluşturma Tarihi:</strong> ${now.toLocaleDateString('tr-TR')}</p>
        <table>
          <tr>
            <th>Kart No</th>
            <th>Site Sakini</th>
            <th>Daire</th>
            <th>Kullanim (TL)</th>
            <th>Limit (TL)</th>
            <th>Durum</th>
          </tr>
    `;

    let totalUsage = 0;
    data.forEach(row => {
      totalUsage += row.monthly_usage;
      html += `
        <tr>
          <td>${row.card_id}</td>
          <td>${row.user_name}</td>
          <td>${row.apartment}</td>
          <td>${row.monthly_usage.toFixed(2)}</td>
          <td>${row.monthly_limit.toFixed(2)}</td>
          <td>${row.is_paid ? 'Odendi' : 'Odenmedi'}</td>
        </tr>
      `;
    });

    html += `
          <tr class="total">
            <td colspan="3">TOPLAM</td>
            <td>${totalUsage.toFixed(2)} TL</td>
            <td colspan="2"></td>
          </tr>
        </table>
      </body>
      </html>
    `;

    // Yeni pencerede aç ve yazdır
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const getUserName = (userId) => {
    if (!userId) return '-';
    const foundUser = users.find(u => u.id === userId);
    return foundUser?.full_name || foundUser?.email || '-';
  };

  const getUserApartment = (userId) => {
    if (!userId) return null;
    const foundUser = users.find(u => u.id === userId);
    return foundUser?.apartment_number || null;
  };

  // Kullanıcıya ait kart sayısını hesapla
  const getUserCardCount = (userId) => {
    if (!userId) return 0;
    return cards.filter(c => c.user_id === userId).length;
  };

  const filteredCards = cards.filter(card => 
    card.card_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Daire bazlı gruplama
  const getCardsGroupedByApartment = () => {
    const grouped = {};
    
    filteredCards.forEach(card => {
      const apt = getUserApartment(card.user_id) || 'Atanmamış';
      if (!grouped[apt]) {
        grouped[apt] = [];
      }
      grouped[apt].push(card);
    });
    
    // Daire numarasına göre sırala (sayısal), "Atanmamış" en sona
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      if (a === 'Atanmamış') return 1;
      if (b === 'Atanmamış') return -1;
      const numA = parseInt(a) || 999999;
      const numB = parseInt(b) || 999999;
      return numA - numB;
    });
    
    return sortedKeys.map(apt => ({
      apartment: apt,
      cards: grouped[apt]
    }));
  };

  const groupedCards = getCardsGroupedByApartment();

  // Ödeme yöntemi etiketleri
  const paymentMethodLabels = {
    cash: 'Nakit',
    bank_transfer: 'Havale',
    credit_card: 'Kredi Kartı',
    eft: 'EFT'
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="rfid-page">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>RFID Kartlar</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>RFID kartları yönetin ve bakiye yükleyin</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {/* Site Sakini ve Çalışan/Şoför için bu butonları gizle */}
          {!hideAdminButtons && (
            <>
              <NativeButton variant="outline" onClick={generateMonthlyReport} data-testid="monthly-report-button">
                <FileText style={{ width: '16px', height: '16px' }} />
                Ay Sonu Raporu
              </NativeButton>
              <NativeButton variant="outline" onClick={() => setNewResidentModal(prev => ({ ...prev, isOpen: true }))} data-testid="new-resident-button">
                <UserPlus style={{ width: '16px', height: '16px' }} />
                Yeni Site Sakini
              </NativeButton>
              <NativeButton onClick={openCreateModal} data-testid="create-rfid-button">
                <Plus style={{ width: '16px', height: '16px' }} />
                Yeni Kart
              </NativeButton>
            </>
          )}
        </div>
      </div>

      {error && (
        <NativeAlert variant="danger" icon={AlertCircle}>
          {error}
        </NativeAlert>
      )}

      {/* Son Okutulan Kartlar Paneli */}
      <NativeCard>
        <NativeCardHeader>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Radio style={{ width: '20px', height: '20px', color: '#8b5cf6' }} />
              <div>
                <NativeCardTitle>Son Okutulan Kartlar</NativeCardTitle>
                <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '13px', margin: '2px 0 0' }}>
                  Son 24 saat ({recentScans.total} okutma • {recentScans.registered_count} kayıtlı • <span style={{ color: '#f59e0b' }}>{recentScans.unregistered_count} kayıtsız</span>)
                </p>
              </div>
            </div>
            <NativeButton variant="outline" size="sm" onClick={fetchRecentScans} disabled={recentScansLoading}>
              {recentScansLoading ? <Loader2 className="animate-spin" style={{ width: '14px', height: '14px' }} /> : <RefreshCw style={{ width: '14px', height: '14px' }} />}
            </NativeButton>
          </div>
        </NativeCardHeader>
        <NativeCardContent>
          {recentScans.scans.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
              <Radio style={{ width: '32px', height: '32px', margin: '0 auto 8px', opacity: 0.5 }} />
              <p style={{ margin: 0 }}>Henüz kart okutması yok</p>
              <p style={{ fontSize: '13px', margin: '4px 0 0' }}>OCPP istasyonlardan kart okutuldugunda burada gorunecek</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {recentScans.scans.slice(0, 12).map((scan, i) => (
                <div 
                  key={`${scan.id_tag}-${i}`}
                  onClick={() => !scan.is_registered && openCreateFromScan(scan)}
                  style={{ 
                    padding: '10px 14px', 
                    borderRadius: '8px', 
                    border: `1px solid ${scan.is_registered ? '#86efac' : '#fcd34d'}`,
                    backgroundColor: scan.is_registered ? '#f0fdf4' : '#fffbeb',
                    cursor: scan.is_registered ? 'default' : 'pointer',
                    transition: 'all 0.2s',
                    minWidth: '200px',
                    flex: '1 1 200px',
                    maxWidth: '300px'
                  }}
                  className={!scan.is_registered ? 'hover:shadow-md' : ''}
                  title={scan.is_registered ? 'Kayitli kart' : 'Tiklayarak kart ekle'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <code style={{ fontSize: '12px', fontWeight: '600', color: scan.is_registered ? '#166534' : '#92400e' }}>
                      {scan.id_tag}
                    </code>
                    {scan.is_registered ? (
                      <Check style={{ width: '14px', height: '14px', color: '#22c55e' }} />
                    ) : (
                      <Plus style={{ width: '14px', height: '14px', color: '#f59e0b' }} />
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Clock style={{ width: '10px', height: '10px' }} />
                      {scan.timestamp ? new Date(scan.timestamp).toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '-'}
                    </div>
                    <div style={{ marginTop: '2px' }}>{scan.station_name || scan.charge_point_id}</div>
                  </div>
                  {scan.user_name && (
                    <div style={{ fontSize: '11px', color: '#166534', marginTop: '4px', fontWeight: '500' }}>
                      {scan.user_name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {recentScans.scans.length > 12 && (
            <p style={{ textAlign: 'center', fontSize: '13px', color: 'var(--text-secondary)', marginTop: '12px' }}>
              +{recentScans.scans.length - 12} daha fazla okutma
            </p>
          )}
        </NativeCardContent>
      </NativeCard>

      {/* Search */}
      <NativeCard>
        <NativeCardContent>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
              <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px' }} className="text-slate-400" />
              <input
                type="text"
                placeholder="Kart ID ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 40px',
                  borderRadius: '8px',
                  border: '1px solid var(--card-border)',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--card-bg)'
                }}
                className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              />
            </div>
            <NativeButton variant="outline" onClick={fetchData}>
              <RefreshCw style={{ width: '16px', height: '16px' }} />
              Yenile
            </NativeButton>
          </div>
        </NativeCardContent>
      </NativeCard>

      {/* Cards Table - Daire Bazlı Gruplandırılmış */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={CreditCard}>
            RFID Kart Listesi ({filteredCards.length})
          </NativeCardTitle>
        </NativeCardHeader>
        <NativeCardContent style={{ padding: 0 }}>
          {filteredCards.length === 0 ? (
            <EmptyState
              icon={CreditCard}
              title="RFID kart bulunamadı"
              description="Henüz RFID kart eklenmemiş"
              action={
                <NativeButton onClick={openCreateModal}>
                  <Plus style={{ width: '16px', height: '16px' }} />
                  İlk Kartı Oluştur
                </NativeButton>
              }
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {groupedCards.map((group, groupIndex) => (
                <div key={group.apartment}>
                  {/* Daire Başlığı */}
                  <div style={{ 
                    padding: '12px 16px', 
                    backgroundColor: group.apartment === 'Atanmamış' ? '#fef3c7' : '#e0f2fe',
                    borderTop: groupIndex > 0 ? '2px solid #e5e7eb' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                  }}>
                    <Home style={{ 
                      width: '18px', 
                      height: '18px', 
                      color: group.apartment === 'Atanmamış' ? '#d97706' : '#0284c7' 
                    }} />
                    <span style={{ 
                      fontWeight: '600', 
                      fontSize: '15px',
                      color: group.apartment === 'Atanmamış' ? '#92400e' : '#0c4a6e'
                    }}>
                      {group.apartment === 'Atanmamış' ? 'Kullanıcı Atanmamış' : `Daire ${group.apartment}`}
                    </span>
                    <span style={{ 
                      fontSize: '13px', 
                      color: group.apartment === 'Atanmamış' ? '#b45309' : '#0369a1',
                      marginLeft: '8px'
                    }}>
                      ({group.cards.length} kart)
                    </span>
                  </div>
                  
                  {/* Daire Kartları */}
                  <NativeTable>
                    <NativeTableHeader>
                      <NativeTableHead>Kart ID</NativeTableHead>
                      <NativeTableHead>Kullanıcı</NativeTableHead>
                      <NativeTableHead>Tip</NativeTableHead>
                      <NativeTableHead>Bakiye / Limit</NativeTableHead>
                      <NativeTableHead>Durum</NativeTableHead>
                      <NativeTableHead align="right">Islemler</NativeTableHead>
                    </NativeTableHeader>
                    <NativeTableBody>
                      {group.cards.map((card) => {
                        const userCardCount = getUserCardCount(card.user_id);
                        return (
                          <NativeTableRow key={card.card_id}>
                            <NativeTableCell>
                              <span style={{ fontFamily: 'monospace', fontWeight: '500' }}>{card.card_id}</span>
                              {userCardCount > 1 && (
                                <span style={{ marginLeft: '6px', fontSize: '10px', color: '#6366f1', backgroundColor: '#eef2ff', padding: '2px 6px', borderRadius: '4px' }}>
                                  +{userCardCount - 1}
                                </span>
                              )}
                            </NativeTableCell>
                            <NativeTableCell>{getUserName(card.user_id)}</NativeTableCell>
                            <NativeTableCell>
                              <NativeBadge variant={card.card_type === 'postpaid' ? 'warning' : 'default'}>
                                {card.card_type === 'postpaid' ? 'Faturali' : 'On Odemeli'}
                              </NativeBadge>
                            </NativeTableCell>
                            <NativeTableCell>
                              {card.card_type === 'postpaid' ? (
                                <div>
                                  <span style={{ fontWeight: '600', color: '#f59e0b' }}>
                                    {(card.monthly_usage || 0).toFixed(2)} / {(card.monthly_limit || 0).toFixed(0)} TL
                                  </span>
                                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Aylik kullanim</div>
                                </div>
                              ) : (
                                <span style={{ fontWeight: '600', color: '#16a34a' }}>{(card.balance || 0).toFixed(2)} TL</span>
                              )}
                            </NativeTableCell>
                            <NativeTableCell>
                              {isCardBlocked(card) ? (
                                <NativeBadge variant="danger">
                                  Bloke
                                </NativeBadge>
                              ) : isCardActive(card) ? (
                                <NativeBadge variant="success">
                                  Aktif
                                </NativeBadge>
                              ) : (
                                <NativeBadge variant="secondary">
                                  Pasif
                                </NativeBadge>
                              )}
                            </NativeTableCell>
                            <NativeTableCell align="right">
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                                {/* Bloke/Aktif Butonu */}
                                <NativeButton 
                                  variant={isCardBlocked(card) ? 'outline' : 'ghost'} 
                                  size="sm" 
                                  onClick={() => toggleCardStatus(card)} 
                                  title={isCardBlocked(card) ? 'Aktif Et' : 'Bloke Et'}
                                  style={isCardBlocked(card) ? { color: '#16a34a' } : { color: '#dc2626' }}
                                  data-testid={`toggle-status-${card.card_id}`}
                                >
                                  {isCardBlocked(card) ? (
                                    <>
                                      <CheckCircle style={{ width: '16px', height: '16px' }} />
                                      <span style={{ marginLeft: '4px', fontSize: '12px' }}>Aktif Et</span>
                                    </>
                                  ) : (
                                    <>
                                      <Ban style={{ width: '16px', height: '16px' }} />
                                      <span style={{ marginLeft: '4px', fontSize: '12px' }}>Bloke Et</span>
                                    </>
                                  )}
                                </NativeButton>
                                <NativeButton variant="outline" size="sm" onClick={() => openStatementModal(card.card_id)} title="Ekstre" data-testid={`statement-rfid-${card.card_id}`}>
                                  <FileText style={{ width: '16px', height: '16px' }} />
                                </NativeButton>
                                <NativeButton variant="outline" size="sm" onClick={() => openCreditModal(card)} data-testid={`credit-rfid-${card.card_id}`}>
                                  <PlusCircle style={{ width: '16px', height: '16px' }} />
                                  Yükle
                                </NativeButton>
                                <NativeButton variant="ghost" size="icon" onClick={() => openEditModal(card)} data-testid={`edit-rfid-${card.card_id}`}>
                                  <Pencil style={{ width: '16px', height: '16px' }} />
                                </NativeButton>
                                <NativeButton variant="ghost" size="icon" onClick={() => openDeleteModal(card)} style={{ color: '#dc2626' }} data-testid={`delete-rfid-${card.card_id}`}>
                                  <Trash2 style={{ width: '16px', height: '16px' }} />
                                </NativeButton>
                              </div>
                            </NativeTableCell>
                          </NativeTableRow>
                        );
                      })}
                    </NativeTableBody>
                  </NativeTable>
                </div>
              ))}
            </div>
          )}
        </NativeCardContent>
      </NativeCard>

      {/* Create/Edit Modal */}
      <NativeModal isOpen={modal.isOpen} onClose={closeModal} title={modal.mode === 'create' ? 'Yeni RFID Kart Oluştur' : 'RFID Kart Düzenle'} disabled={modal.saving}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <NativeInput label="Kart ID" id="card_id" value={form.card_id} onChange={(e) => updateForm('card_id', e.target.value)} placeholder="RFID-001" required disabled={modal.mode === 'edit'} />
            </div>
            {modal.mode === 'create' && (
              <NativeButton type="button" variant="outline" onClick={openSelectFromScansModal} style={{ marginBottom: '12px', whiteSpace: 'nowrap' }}>
                <Radio style={{ width: '14px', height: '14px' }} />
                Son Okutulanlar
              </NativeButton>
            )}
          </div>
          
          {isAdmin() && (
            <NativeSelect 
              label="Kullanıcı" 
              id="user_id" 
              value={form.user_id} 
              onChange={(e) => updateForm('user_id', e.target.value)} 
              placeholder="Atanmamis" 
              options={[
                { value: '', label: '-- Atanmamış --' },
                ...users.map((u) => ({ 
                  value: u.id, 
                  label: `${u.full_name || u.email}${u.apartment_number ? ` (Daire: ${u.apartment_number})` : ''}` 
                }))
              ]} 
            />
          )}

          <NativeSelect label="Durum" id="status" value={form.status} onChange={(e) => updateForm('status', e.target.value)} options={[{ value: 'active', label: 'Aktif' }, { value: 'inactive', label: 'Pasif' }, { value: 'blocked', label: 'Bloke' }]} />

          <NativeSelect 
            label="Kart Tipi" 
            id="card_type" 
            value={form.card_type} 
            onChange={(e) => updateForm('card_type', e.target.value)} 
            options={[
              { value: 'prepaid', label: 'On Odemeli (Bakiye)' }, 
              { value: 'postpaid', label: 'Faturali (Aylik)' }
            ]} 
          />

          {form.card_type === 'prepaid' && (
            <NativeInput 
              label="Baslangic Bakiyesi (TL)" 
              id="balance" 
              type="number" 
              min="0"
              step="10"
              value={form.balance} 
              onChange={(e) => updateForm('balance', parseFloat(e.target.value) || 0)} 
              placeholder="0"
            />
          )}

          {form.card_type === 'postpaid' && (
            <NativeInput 
              label="Aylik Limit (TL)" 
              id="monthly_limit" 
              type="number" 
              min="0"
              step="100"
              value={form.monthly_limit} 
              onChange={(e) => updateForm('monthly_limit', parseFloat(e.target.value) || 0)} 
              placeholder="0 = Limitsiz"
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '8px' }}>
            <NativeButton type="button" variant="outline" onClick={closeModal} disabled={modal.saving}>Iptal</NativeButton>
            <NativeButton type="submit" disabled={modal.saving}>
              {modal.saving ? (<><Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />Kaydediliyor...</>) : (modal.mode === 'create' ? 'Oluştur' : 'Kaydet')}
            </NativeButton>
          </div>
        </form>
      </NativeModal>

      {/* Select From Scans Modal */}
      <NativeModal isOpen={selectFromScansModal.isOpen} onClose={() => setSelectFromScansModal({ isOpen: false, unregisteredCards: [] })} title="Son Okutulan Kayitsiz Kartlar">
        {selectFromScansModal.unregisteredCards.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
            <CreditCard style={{ width: '32px', height: '32px', margin: '0 auto 8px', opacity: 0.5 }} />
            <p style={{ margin: 0 }}>Kayıtsız kart bulunamadı</p>
            <p style={{ fontSize: '13px', margin: '4px 0 0' }}>Son 24 saatte okutulan tum kartlar zaten sisteme kayıtlı</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {selectFromScansModal.unregisteredCards.map((card, i) => (
              <div 
                key={`unregistered-${card.id_tag}-${i}`}
                onClick={() => selectCardFromScans(card)}
                style={{ 
                  padding: '12px 16px', 
                  borderRadius: '8px', 
                  border: '1px solid #fcd34d',
                  backgroundColor: '#fffbeb',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                className="hover:shadow-md hover:border-amber-400"
              >
                <div>
                  <code style={{ fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                    {card.id_tag}
                  </code>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    {card.station_name} • {card.timestamp ? new Date(card.timestamp).toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '-'}
                  </div>
                </div>
                <NativeButton variant="outline" size="sm">
                  <Plus style={{ width: '14px', height: '14px' }} />
                  Sec
                </NativeButton>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
          <NativeButton variant="outline" onClick={() => setSelectFromScansModal({ isOpen: false, unregisteredCards: [] })}>
            Kapat
          </NativeButton>
        </div>
      </NativeModal>

      {/* Credit Modal - Genisletilmis */}
      <NativeModal isOpen={creditModal.isOpen} onClose={closeCreditModal} title="Bakiye Yükle" disabled={creditModal.saving}>
        <form onSubmit={handleCreditSubmit}>
          {/* Kart Numarası */}
          <div style={{ 
            padding: '12px 16px', 
            backgroundColor: '#1e293b', 
            borderRadius: '8px', 
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <CreditCard style={{ width: '24px', height: '24px', color: '#60a5fa' }} />
            <div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Kart Numarası</div>
              <code style={{ fontSize: '18px', fontWeight: '700', color: '#f8fafc', letterSpacing: '1px' }}>
                {creditModal.card?.card_id}
              </code>
            </div>
          </div>

          {/* Kart Sahibi Bilgileri */}
          {creditModal.ownerInfo && creditModal.ownerInfo.owner_name && (
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#f0fdf4', 
              borderRadius: '8px', 
              marginBottom: '16px',
              border: '1px solid #86efac'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <User style={{ width: '16px', height: '16px', color: '#16a34a' }} />
                <span style={{ fontWeight: '600', color: '#166534' }}>Kart Sahibi Bilgileri</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
                <div>
                  <span style={{ color: '#6b7280' }}>Ad Soyad:</span>
                  <div style={{ fontWeight: '600', color: '#166534', fontSize: '15px' }}>{creditModal.ownerInfo.owner_name}</div>
                </div>
                <div>
                  <span style={{ color: '#6b7280' }}>Daire:</span>
                  <div style={{ fontWeight: '600', color: '#166534', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Home style={{ width: '14px', height: '14px' }} />
                    {creditModal.ownerInfo.apartment_number || '-'}
                  </div>
                </div>
                {creditModal.ownerInfo.account_name && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <span style={{ color: '#6b7280' }}>Hesap Adi:</span>
                    <div style={{ fontWeight: '500', color: '#166534' }}>{creditModal.ownerInfo.account_name}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Kullanıcı atanmamış uyarısı */}
          {creditModal.ownerInfo && !creditModal.ownerInfo.owner_name && (
            <div style={{ 
              padding: '12px 16px', 
              backgroundColor: '#fef3c7', 
              borderRadius: '8px', 
              marginBottom: '16px',
              border: '1px solid #fcd34d',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertTriangle style={{ width: '16px', height: '16px', color: '#d97706' }} />
              <span style={{ color: '#92400e', fontSize: '14px' }}>Bu kart henüz bir kullanıcıya atanmamış</span>
            </div>
          )}

          {/* Mevcut Bakiye */}
          <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', textAlign: 'center', marginBottom: '16px' }} className="dark:bg-slate-800">
            <p style={{ fontSize: '14px', margin: '0 0 8px', color: '#64748b' }}>Mevcut Bakiye</p>
            <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#16a34a', margin: 0 }}>{(creditModal.card?.balance || 0).toFixed(2)} TL</p>
          </div>

          <NativeInput 
            label="Yüklenecek Tutar (TL)" 
            id="credit_amount" 
            type="number" 
            step="0.01" 
            min="0.01" 
            value={creditModal.amount} 
            onChange={(e) => setCreditModal(prev => ({ ...prev, amount: e.target.value }))} 
            placeholder="100.00" 
            required 
          />

          <NativeSelect 
            label="Odeme Yontemi" 
            id="payment_method" 
            value={creditModal.paymentMethod} 
            onChange={(e) => setCreditModal(prev => ({ ...prev, paymentMethod: e.target.value }))}
            options={[
              { value: 'cash', label: 'Nakit' },
              { value: 'bank_transfer', label: 'Havale' },
              { value: 'credit_card', label: 'Kredi Karti' },
              { value: 'eft', label: 'EFT' }
            ]}
          />

          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
              Not (Istege Bagli)
            </label>
            <textarea
              value={creditModal.note}
              onChange={(e) => setCreditModal(prev => ({ ...prev, note: e.target.value }))}
              placeholder="Ornek: Agustos ayi odemesi"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid var(--card-border)',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
                minHeight: '80px',
                resize: 'vertical',
                color: 'var(--text-primary)',
                backgroundColor: 'var(--card-bg)'
              }}
              className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '8px' }}>
            <NativeButton type="button" variant="outline" onClick={closeCreditModal} disabled={creditModal.saving}>Iptal</NativeButton>
            <NativeButton type="submit" disabled={creditModal.saving}>
              {creditModal.saving ? (<><Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />Yükleniyor...</>) : 'Bakiye Yükle'}
            </NativeButton>
          </div>
        </form>
      </NativeModal>

      {/* Statement Modal - Kart Ekstresi */}
      <NativeModal 
        isOpen={statementModal.isOpen} 
        onClose={closeStatementModal} 
        title={`Kart Ekstresi - ${statementModal.cardId || ''}`}
        size="lg"
      >
        {statementModal.loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px' }}>
            <Loader2 className="animate-spin" style={{ width: '32px', height: '32px', color: '#3b82f6' }} />
          </div>
        ) : statementModal.data ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Kart Sahibi Bilgileri */}
            {statementModal.data.owner && (
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f0fdf4', 
                borderRadius: '8px',
                border: '1px solid #86efac'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <User style={{ width: '16px', height: '16px', color: '#16a34a' }} />
                  <span style={{ fontWeight: '600', color: '#166534' }}>Kart Sahibi</span>
                </div>
                <div style={{ fontSize: '14px' }}>
                  <strong>{statementModal.data.owner.full_name}</strong>
                  {statementModal.data.owner.apartment_number && (
                    <span style={{ marginLeft: '8px', color: '#6b7280' }}>
                      <Home style={{ width: '12px', height: '12px', display: 'inline', marginRight: '4px' }} />
                      Daire {statementModal.data.owner.apartment_number}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Ozet Kartlari */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
              <div style={{ padding: '16px', backgroundColor: '#ecfdf5', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#059669', marginBottom: '4px' }}>Mevcut Bakiye</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#047857' }}>
                  {statementModal.data.current_balance?.toFixed(2)} TL
                </div>
              </div>
              <div style={{ padding: '16px', backgroundColor: '#dbeafe', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#2563eb', marginBottom: '4px' }}>Toplam Yüklenen</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1d4ed8' }}>
                  {statementModal.data.statistics?.total_loaded?.toFixed(2)} TL
                </div>
              </div>
              <div style={{ padding: '16px', backgroundColor: '#fef3c7', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#d97706', marginBottom: '4px' }}>Toplam Harcanan</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#b45309' }}>
                  {statementModal.data.statistics?.total_charged?.toFixed(2)} TL
                </div>
              </div>
              <div style={{ padding: '16px', backgroundColor: '#ede9fe', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#7c3aed', marginBottom: '4px' }}>Toplam Enerji</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#6d28d9' }}>
                  {statementModal.data.statistics?.total_energy_kwh?.toFixed(2)} kWh
                </div>
              </div>
            </div>

            {/* İşlem Geçmişi */}
            <div>
              <h4 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '600' }}>İşlem Geçmişi</h4>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {statementModal.data.transactions?.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                    Henüz işlem yok
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {statementModal.data.transactions?.map((tx, i) => (
                      <div 
                        key={i}
                        style={{ 
                          padding: '12px 16px', 
                          backgroundColor: tx.type === 'credit' ? '#f0fdf4' : tx.type === 'charging' ? '#fef3c7' : '#fee2e2',
                          borderRadius: '8px',
                          border: `1px solid ${tx.type === 'credit' ? '#86efac' : tx.type === 'charging' ? '#fcd34d' : '#fca5a5'}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            {tx.type === 'credit' ? (
                              <TrendingUp style={{ width: '14px', height: '14px', color: '#16a34a' }} />
                            ) : tx.type === 'charging' ? (
                              <Zap style={{ width: '14px', height: '14px', color: '#d97706' }} />
                            ) : (
                              <TrendingDown style={{ width: '14px', height: '14px', color: '#dc2626' }} />
                            )}
                            <span style={{ fontWeight: '500', fontSize: '14px' }}>{tx.description}</span>
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {tx.date ? new Date(tx.date).toLocaleString('tr-TR', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              year: 'numeric',
                              hour: '2-digit', 
                              minute: '2-digit' 
                            }) : '-'}
                            {tx.payment_method && <span style={{ marginLeft: '8px' }}>• {paymentMethodLabels[tx.payment_method] || tx.payment_method}</span>}
                            {tx.admin_name && <span style={{ marginLeft: '8px' }}>• {tx.admin_name}</span>}
                            {tx.energy_kwh > 0 && <span style={{ marginLeft: '8px' }}>• {tx.energy_kwh?.toFixed(2)} kWh</span>}
                            {tx.duration_minutes && <span style={{ marginLeft: '8px' }}>• {tx.duration_minutes} dk</span>}
                          </div>
                          {tx.note && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', fontStyle: 'italic' }}>
                              "{tx.note}"
                            </div>
                          )}
                        </div>
                        <div style={{ 
                          fontWeight: 'bold', 
                          fontSize: '16px',
                          color: tx.amount >= 0 ? '#16a34a' : '#dc2626'
                        }}>
                          {tx.amount >= 0 ? '+' : ''}{tx.amount?.toFixed(2)} TL
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
            Veri yüklenemedi
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
          <NativeButton variant="outline" onClick={closeStatementModal}>
            Kapat
          </NativeButton>
        </div>
      </NativeModal>

      {/* Delete Modal */}
      <NativeModal isOpen={deleteModal.isOpen} onClose={closeDeleteModal} title="RFID Kart Silme Onayi" disabled={deleteModal.deleting}>
        <div>
          <NativeAlert variant="warning" icon={AlertTriangle}>
            <p style={{ fontWeight: '500', marginBottom: '4px' }}>Dikkat!</p>
            <p style={{ margin: 0, fontSize: '14px' }}>Bu RFID karti kalici olarak silinecektir. Kart bakiyesi de silinecektir.</p>
          </NativeAlert>
          
          {deleteModal.card && (
            <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', marginTop: '16px' }} className="dark:bg-slate-800">
              <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', marginBottom: '8px' }}>Silinecek kart:</p>
              <p className="text-slate-900 dark:text-white" style={{ fontWeight: '500', fontFamily: 'monospace', margin: 0 }}>{deleteModal.card.card_id}</p>
              <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', marginTop: '4px' }}>
                Bakiye: <span style={{ fontWeight: '600', color: '#16a34a' }}>{(deleteModal.card.balance || 0).toFixed(2)} TL</span>
              </p>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
            <NativeButton type="button" variant="outline" onClick={closeDeleteModal} disabled={deleteModal.deleting}>Iptal</NativeButton>
            <NativeButton type="button" variant="danger" onClick={handleDelete} disabled={deleteModal.deleting} data-testid="confirm-delete-rfid">
              {deleteModal.deleting ? (<><Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />Siliniyor...</>) : 'Sil'}
            </NativeButton>
          </div>
        </div>
      </NativeModal>

      {/* Yeni Site Sakini + Kart Modal */}
      <NativeModal 
        isOpen={newResidentModal.isOpen} 
        onClose={() => !newResidentModal.saving && setNewResidentModal(prev => ({ ...prev, isOpen: false }))} 
        title="Yeni Site Sakini Ekle"
        disabled={newResidentModal.saving}
      >
        <form onSubmit={handleCreateResident}>
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#e0f2fe', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0369a1' }}>
              <UserPlus style={{ width: '16px', height: '16px' }} />
              <span style={{ fontWeight: '500' }}>Site Sakini bilgileri ve RFID kart ayni anda tanimlanir</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <NativeInput 
              label="Ad Soyad" 
              value={newResidentModal.form.full_name} 
              onChange={(e) => setNewResidentModal(prev => ({ 
                ...prev, 
                form: { ...prev.form, full_name: e.target.value } 
              }))} 
              required 
            />
            <NativeInput 
              label="Daire No" 
              value={newResidentModal.form.apartment_number} 
              onChange={(e) => setNewResidentModal(prev => ({ 
                ...prev, 
                form: { ...prev.form, apartment_number: e.target.value } 
              }))} 
              placeholder="Ornek: 5"
              required 
            />
          </div>

          <NativeInput 
            label="E-posta" 
            type="email"
            value={newResidentModal.form.email} 
            onChange={(e) => setNewResidentModal(prev => ({ 
              ...prev, 
              form: { ...prev.form, email: e.target.value } 
            }))} 
            required 
          />

          <NativeInput 
            label="Sifre (Bos birakilirsa: Hims2024!)" 
            type="password"
            value={newResidentModal.form.password} 
            onChange={(e) => setNewResidentModal(prev => ({ 
              ...prev, 
              form: { ...prev.form, password: e.target.value } 
            }))} 
            placeholder="Bos birakilabilir"
          />

          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '16px', paddingTop: '16px' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
              RFID Kart Bilgileri (Istege Bagli)
            </h4>
            
            <NativeInput 
              label="Kart Numarasi" 
              value={newResidentModal.form.card_id} 
              onChange={(e) => setNewResidentModal(prev => ({ 
                ...prev, 
                form: { ...prev.form, card_id: e.target.value } 
              }))} 
              placeholder="Sonra da eklenebilir"
            />

            <NativeSelect 
              label="Kart Tipi" 
              value={newResidentModal.form.card_type} 
              onChange={(e) => setNewResidentModal(prev => ({ 
                ...prev, 
                form: { ...prev.form, card_type: e.target.value } 
              }))} 
              options={[
                { value: 'prepaid', label: 'On Odemeli' },
                { value: 'postpaid', label: 'Faturali' }
              ]}
            />

            {newResidentModal.form.card_type === 'prepaid' && newResidentModal.form.card_id && (
              <NativeInput 
                label="Baslangic Bakiyesi (TL)" 
                type="number"
                value={newResidentModal.form.balance} 
                onChange={(e) => setNewResidentModal(prev => ({ 
                  ...prev, 
                  form: { ...prev.form, balance: e.target.value } 
                }))} 
              />
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
            <NativeButton 
              type="button" 
              variant="outline" 
              onClick={() => setNewResidentModal(prev => ({ ...prev, isOpen: false }))}
              disabled={newResidentModal.saving}
            >
              Iptal
            </NativeButton>
            <NativeButton type="submit" disabled={newResidentModal.saving}>
              {newResidentModal.saving ? (
                <><Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} /> Kaydediliyor...</>
              ) : (
                <><UserPlus style={{ width: '16px', height: '16px' }} /> Site Sakini Oluştur</>
              )}
            </NativeButton>
          </div>
        </form>
      </NativeModal>

      {/* Ay Sonu Raporu Modal */}
      <NativeModal 
        isOpen={monthlyReportModal.isOpen} 
        onClose={() => setMonthlyReportModal({ isOpen: false, loading: false, data: null })} 
        title="Faturali Kartlar - Ay Sonu Raporu"
        size="lg"
      >
        {monthlyReportModal.data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '16px' }}>
                  {new Date().toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                </h4>
                <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#6b7280' }}>
                  {monthlyReportModal.data.length} faturali kart
                </p>
              </div>
              <NativeButton variant="outline" onClick={downloadReportPDF}>
                <Download style={{ width: '16px', height: '16px' }} />
                PDF Indir
              </NativeButton>
            </div>

            {monthlyReportModal.data.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
                <CreditCard style={{ width: '32px', height: '32px', margin: '0 auto 8px', opacity: 0.5 }} />
                <p>Faturalı kart bulunamadı</p>
              </div>
            ) : (
              <>
                <NativeTable>
                  <NativeTableHeader>
                    <NativeTableHead>Kart</NativeTableHead>
                    <NativeTableHead>Site Sakini</NativeTableHead>
                    <NativeTableHead>Daire</NativeTableHead>
                    <NativeTableHead>Kullanim</NativeTableHead>
                    <NativeTableHead>Limit</NativeTableHead>
                    <NativeTableHead>Durum</NativeTableHead>
                  </NativeTableHeader>
                  <NativeTableBody>
                    {monthlyReportModal.data.map((row, i) => (
                      <NativeTableRow key={row.card_id}>
                        <NativeTableCell>
                          <code style={{ fontSize: '12px' }}>{row.card_id}</code>
                        </NativeTableCell>
                        <NativeTableCell>{row.user_name}</NativeTableCell>
                        <NativeTableCell>{row.apartment}</NativeTableCell>
                        <NativeTableCell>
                          <span style={{ fontWeight: '600', color: '#d97706' }}>
                            {row.monthly_usage.toFixed(2)} TL
                          </span>
                        </NativeTableCell>
                        <NativeTableCell>{row.monthly_limit.toFixed(0)} TL</NativeTableCell>
                        <NativeTableCell>
                          <NativeButton 
                            variant={row.is_paid ? 'success' : 'outline'}
                            size="sm"
                            onClick={() => {
                              const newData = [...monthlyReportModal.data];
                              newData[i].is_paid = !newData[i].is_paid;
                              setMonthlyReportModal(prev => ({ ...prev, data: newData }));
                            }}
                          >
                            {row.is_paid ? (
                              <><Check style={{ width: '14px', height: '14px' }} /> Odendi</>
                            ) : (
                              'Odenmedi'
                            )}
                          </NativeButton>
                        </NativeTableCell>
                      </NativeTableRow>
                    ))}
                  </NativeTableBody>
                </NativeTable>

                {/* Toplam */}
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: '#fef3c7', 
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '600', color: '#92400e' }}>TOPLAM</span>
                  <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#b45309' }}>
                    {monthlyReportModal.data.reduce((sum, r) => sum + r.monthly_usage, 0).toFixed(2)} TL
                  </span>
                </div>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <NativeButton variant="outline" onClick={() => setMonthlyReportModal({ isOpen: false, loading: false, data: null })}>
                Kapat
              </NativeButton>
            </div>
          </div>
        )}
      </NativeModal>
    </div>
  );
}
